import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useIsMobile } from "senf-atomic-design-system";
import { getInfoPageSlug, useGlobalState } from "senf-shared";
import type { Project } from "senf-shared/types";
import { cn } from "../../../util/classNameConstructor";
import Transition from "../../atoms/animations/Transition";
import Button from "../../core/button/Button";
import LanguageButton from "../../molecules/languageSettings/LanguageButton";
import AccessibilitySettings from "../accessibilitySettings/AccessibilitySettings";

interface InfoPageNavProps {
  project: Project;
  projectId: string;
  currentInfoPage: {
    uid: string;
    navigationLabel?: string;
    title?: string;
    isHidden?: boolean;
  } | null;
  imageAnimation: {
    backgroundOpacity: number;
  };
  isNavVisible: boolean;
  isPreview?: boolean;
  onPageChange?: (slug: string) => void;
  isMobile?: boolean;
}

const InfoPageNav = ({
  project,
  projectId,
  currentInfoPage,
  // imageAnimation, // Unused parameter
  isNavVisible,
  isPreview = false,
  onPageChange,
  isMobile: isMobileProp,
}: InfoPageNavProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pageSlug } = useParams();
  const isMobileDevice = useIsMobile();
  const [translation] = useGlobalState().translationsState;
  // Use provided isMobile prop in preview mode, otherwise use device detection
  const isMobile =
    isPreview && isMobileProp !== undefined ? isMobileProp : isMobileDevice;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [focusedTabIndex, setFocusedTabIndex] = useState(0);
  // Track active tab index for preview mode
  const [previewActiveIndex, setPreviewActiveIndex] = useState(0);
  const navRef = useRef<HTMLElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const announcementRef = useRef<HTMLDivElement>(null);
  const hasInitializedPreviewIndex = useRef(false);

  // Helper function to get flattened infoPages array (same as in InfoPage.tsx)
  const getFlattenedInfoPages = useMemo(() => {
    console.log("getFlattenedInfoPages", project?.infoPages);
    const infoPagesUnknown = project?.infoPages as unknown;
    if (!Array.isArray(infoPagesUnknown))
      return [] as Array<{
        uid: string;
        navigationLabel?: string;
        title?: string;
        isHidden?: boolean;
      }>;

    const first = (infoPagesUnknown as unknown[])[0];
    let allPages: Array<{
      uid: string;
      navigationLabel?: string;
      title?: string;
      isHidden?: boolean;
    }> = [];

    // New structure: first entry is an object with a pages array
    if (
      first &&
      typeof first === "object" &&
      Array.isArray((first as { pages?: unknown[] }).pages)
    ) {
      allPages = ((first as { pages: unknown[] }).pages || []) as Array<{
        uid: string;
        navigationLabel?: string;
        title?: string;
        isHidden?: boolean;
      }>;
    }
    // Grouped structure: first entry is an array of pages
    else if (Array.isArray(first)) {
      allPages = first as Array<{
        uid: string;
        navigationLabel?: string;
        title?: string;
        isHidden?: boolean;
      }>;
    }
    // Legacy: array of pages directly
    else {
      allPages = infoPagesUnknown as Array<{
        uid: string;
        navigationLabel?: string;
        title?: string;
        isHidden?: boolean;
      }>;
    }

    // Filter out hidden pages from navigation
    return allPages.filter((page) => !page.isHidden);
  }, [JSON.stringify(project?.infoPages)]);

  // Initialize preview active index based on currentInfoPage
  useEffect(() => {
    if (isPreview && currentInfoPage && !hasInitializedPreviewIndex.current) {
      const currentIndex = getFlattenedInfoPages.findIndex((page) => {
        return (
          (page.uid || (page as any).infoPageId) ===
          (currentInfoPage.uid || (currentInfoPage as any).infoPageId)
        );
      });
      if (currentIndex !== -1) {
        setPreviewActiveIndex(currentIndex);
        hasInitializedPreviewIndex.current = true;
      }
    }
    // Reset initialization flag when exiting preview mode
    if (!isPreview && hasInitializedPreviewIndex.current) {
      hasInitializedPreviewIndex.current = false;
      setPreviewActiveIndex(0);
    }
  }, [isPreview, currentInfoPage, getFlattenedInfoPages]);

  const handleTabClick = (slug: string, pageIndex: number) => {
    console.log("Tab clicked:", slug, "pageIndex:", pageIndex);

    // Announce navigation change to screen readers
    const page = getFlattenedInfoPages[pageIndex];
    const pageLabel = page?.navigationLabel;
    if (announcementRef.current) {
      announcementRef.current.textContent = `${t("Navigated to")} ${pageLabel}`;
    }

    // Scroll to top when tab is clicked
    const scrollContainer = document.querySelector("[data-infopage-scroll]");
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    }

    if (isPreview && onPageChange) {
      setPreviewActiveIndex(pageIndex);
      onPageChange(slug);
    } else {
      // If it's the first page (index 0), navigate to /info without slug
      if (pageIndex === 0) {
        navigate(`/projects/${projectId}/info`);
      } else {
        navigate(`/projects/${projectId}/info/${slug}`);
      }
    }
    if (isMobile) {
      setIsMobileNavOpen(false);
    }
  };

  const toggleMobileNav = () => {
    if (!isMobileNavOpen) {
      // Store the currently focused element before opening
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  // Focus management for mobile navigation
  useEffect(() => {
    if (isMobileNavOpen && mobileNavRef.current) {
      // Focus the first navigation item when mobile nav opens
      const firstNavItem = mobileNavRef.current.querySelector(
        'button[type="button"]',
      ) as HTMLElement;
      if (firstNavItem) {
        firstNavItem.focus();
      }
    } else if (!isMobileNavOpen && previousFocusRef.current) {
      // Restore focus to the previously focused element when mobile nav closes
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isMobileNavOpen]);

  // Scroll lock for info page container when mobile navigation is open
  useEffect(() => {
    if (isMobileNavOpen) {
      const scrollContainer = document.querySelector("[data-infopage-scroll]");
      if (scrollContainer) {
        // Store original overflow style and lock scroll
        const originalOverflow = (scrollContainer as HTMLElement).style
          .overflow;
        (scrollContainer as HTMLElement).style.overflow = "hidden";

        return () => {
          // Restore original overflow when closing or unmounting
          (scrollContainer as HTMLElement).style.overflow = originalOverflow;
        };
      }
    }
  }, [isMobileNavOpen]);

  // Handle keyboard navigation for desktop tabs
  const handleDesktopKeyDown = (event: React.KeyboardEvent) => {
    const tabs = getFlattenedInfoPages;
    const currentIndex = focusedTabIndex;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowLeft": {
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        const nextIndex =
          (currentIndex + direction + tabs.length) % tabs.length;
        setFocusedTabIndex(nextIndex);
        // Focus the new tab after state update
        setTimeout(() => {
          const tabButtons = navRef.current?.querySelectorAll('[role="tab"]');
          if (tabButtons?.[nextIndex]) {
            (tabButtons[nextIndex] as HTMLElement).focus();
          }
        }, 0);
        break;
      }
      case "Home": {
        event.preventDefault();
        setFocusedTabIndex(0);
        setTimeout(() => {
          const tabButtons = navRef.current?.querySelectorAll('[role="tab"]');
          if (tabButtons?.[0]) {
            (tabButtons[0] as HTMLElement).focus();
          }
        }, 0);
        break;
      }
      case "End": {
        event.preventDefault();
        setFocusedTabIndex(tabs.length - 1);
        setTimeout(() => {
          const tabButtons = navRef.current?.querySelectorAll('[role="tab"]');
          if (tabButtons?.[tabs.length - 1]) {
            (tabButtons[tabs.length - 1] as HTMLElement).focus();
          }
        }, 0);
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        const currentPage = tabs[currentIndex];
        if (currentPage) {
          handleTabClick(getInfoPageSlug(currentPage), currentIndex);
        }
        break;
      }
    }
  };

  // Update focused tab index when current page changes
  useEffect(() => {
    const currentIndex = getFlattenedInfoPages.findIndex((page) => {
      if (!pageSlug && getFlattenedInfoPages.indexOf(page) === 0) {
        return true;
      }
      return (
        currentInfoPage &&
        getInfoPageSlug(currentInfoPage) === getInfoPageSlug(page)
      );
    });
    if (currentIndex !== -1) {
      setFocusedTabIndex(currentIndex);
    }
  }, [currentInfoPage, pageSlug, getFlattenedInfoPages]);

  // Don't render if there's only one info page or no info pages
  // Also don't render if the current page is hidden
  if (currentInfoPage?.isHidden) {
    return null;
  }

  // Desktop Navigation
  if (!isMobile) {
    if (!getFlattenedInfoPages || getFlattenedInfoPages.length <= 1) {
      return null;
    }
    return (
      <>
        {/* Screen reader announcements */}
        <div
          ref={announcementRef}
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        />
        <nav
          ref={navRef}
          aria-label={t("Page navigation")}
          className={cn(
            "absolute top-0 left-0 z-[999999] flex justify-end transition-transform duration-300 ease-in-out w-full",
            isNavVisible ? "translate-y-0" : "-translate-y-full",
          )}
        >
          <div className="z-[999999] bg-white h-[76px] w-full flex justify-end items-center border-b mx-auto gap-2">
            <div className="w-1/2">
              <div
                role="tablist"
                aria-label={t("Information pages")}
                className="ps-3 flex max-w-2xl mx-auto start-0 h-[54px] items-center gap-1"
                onKeyDown={handleDesktopKeyDown}
              >
                {getFlattenedInfoPages.map((page, index) => {
                  const isActive = (() => {
                    // In preview mode, use the tracked active index
                    if (isPreview) {
                      return index === previewActiveIndex;
                    }

                    // In normal mode, use URL-based logic
                    // If no pageSlug in URL and this is the first page
                    if (!pageSlug && index === 0) {
                      return true;
                    }
                    // Otherwise check if current page slug matches
                    return (
                      currentInfoPage &&
                      getInfoPageSlug(currentInfoPage) === getInfoPageSlug(page)
                    );
                  })();

                  const isFocused = focusedTabIndex === index;
                  const currentPageSlug = getInfoPageSlug(page);
                  const pageLabel =
                    translation?.[page.navigationLabel] || page.navigationLabel;

                  return (
                    <Button
                      key={page.uid}
                      text={pageLabel}
                      variant="shapeless"
                      size="lg"
                      onClick={() => handleTabClick(currentPageSlug, index)}
                      onKeyDown={handleDesktopKeyDown}
                      role="tab"
                      aria-selected={isActive || undefined}
                      aria-controls={`info-page-${currentPageSlug}`}
                      tabIndex={isFocused ? 0 : -1}
                      aria-label={`${t("Go to")} ${pageLabel}${isActive ? ` (${t("current page")})` : ""}`}
                      className={cn(
                        "text-lg font-semibold transition-colors hover:text-text-default",
                        isActive ? "text-text-default" : "text-text-subdued",
                      )}
                    />
                  );
                })}
              </div>
            </div>
            <div
              className="w-auto ms-auto flex gap-2 pe-5"
              role="toolbar"
              aria-label={t("Page settings")}
            >
              {project?.settings?.accessibility ? (
                <AccessibilitySettings project={project} variant="outline" />
              ) : null}
              {project?.settings?.languages?.length ? (
                <LanguageButton project={project} variant="outline" />
              ) : null}
            </div>
          </div>
        </nav>
      </>
    );
  }

  // Mobile Navigation

  if (getFlattenedInfoPages?.length === 1)
    return (
      <header
        className={cn(
          "absolute z-[999999] flex gap-2 top-5 end-5 transition-transform duration-300 ease-in-out",
          isNavVisible ? "translate-y-0" : "-translate-y-[80px]",
        )}
      >
        {project?.settings?.languages?.length ? (
          <LanguageButton project={project} />
        ) : null}
        {project?.settings?.accessibility ? (
          <AccessibilitySettings project={project} />
        ) : null}
      </header>
    );

  return (
    <>
      {/* Screen reader announcements */}
      <div
        ref={announcementRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      {/* Mobile Header with Menu Button */}
      <header
        className={cn(
          "absolute z-[999999] flex gap-2 top-5 end-5 transition-transform duration-300 ease-in-out",
          isNavVisible ? "translate-y-0" : "-translate-y-[80px]",
        )}
      >
        <Button
          text={t("global:menu")}
          variant="outline"
          trailingIcon="hamburger"
          onClick={toggleMobileNav}
          className="bg-white"
          aria-label={t("util:open", { val: t("global:menu") })}
          aria-expanded={isMobileNavOpen}
          aria-controls="mobile-navigation-panel"
          aria-haspopup="true"
        />
      </header>

      {/* Mobile Navigation Overlay */}
      <Transition
        show={isMobileNavOpen}
        from={{ opacity: 0 }}
        enter={{ opacity: 1 }}
        leave={{ opacity: 0 }}
        config={{ tension: 200, friction: 23 }}
        className="fixed inset-0 z-[999999]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-navigation-title"
      >
        {/* Backdrop */}
        <button
          className="fixed inset-0 bg-black bg-opacity-50 w-full h-full z-1"
          onClick={toggleMobileNav}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              toggleMobileNav();
            }
          }}
          aria-label={t("Close navigation")}
          type="button"
        />

        {/* Slide-out Panel */}
        <Transition
          show={isMobileNavOpen}
          from={{
            transform:
              document.dir === "rtl" ? "translateX(-100%)" : "translateX(100%)",
          }}
          enter={{ transform: "translateX(0%)" }}
          leave={{
            transform:
              document.dir === "rtl" ? "translateX(-100%)" : "translateX(100%)",
          }}
          config={{ tension: 200, friction: 23 }}
          className="fixed top-0 end-0 h-full w-5/6 bg-white shadow-lg"
        >
          <div
            ref={mobileNavRef}
            id="mobile-navigation-panel"
            className="h-full flex flex-col"
          >
            {/* Close Button */}
            <div className="flex justify-end p-4 rtl:justify-start">
              <button
                onClick={toggleMobileNav}
                aria-label={t("Close navigation")}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                type="button"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Close</title>
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Navigation Tabs */}
            <nav
              className="flex-1 px-8 pt-5 rtl:text-right"
              aria-label={t("Information pages")}
            >
              <ul className="space-y-1">
                {getFlattenedInfoPages.map((page, index) => {
                  const isActive = (() => {
                    // In preview mode, use the tracked active index
                    if (isPreview) {
                      return index === previewActiveIndex;
                    }

                    // In normal mode, use URL-based logic
                    // If no pageSlug in URL and this is the first page
                    if (!pageSlug && index === 0) {
                      return true;
                    }
                    // Otherwise check if current page slug matches
                    return (
                      currentInfoPage &&
                      getInfoPageSlug(currentInfoPage) === getInfoPageSlug(page)
                    );
                  })();

                  const currentPageSlug = getInfoPageSlug(page);
                  const pageLabel =
                    translation?.[page.navigationLabel] || page.navigationLabel;

                  return (
                    <li key={page.uid}>
                      <button
                        onClick={() => handleTabClick(currentPageSlug, index)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleTabClick(currentPageSlug, index);
                          }
                        }}
                        className={cn(
                          "w-full text-start py-5 transition-colors focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2",
                          isActive
                            ? "text-gray-900 font-bold"
                            : "text-gray-600 font-normal hover:text-gray-900",
                        )}
                        type="button"
                        aria-current={isActive ? "page" : undefined}
                        aria-label={`${t("Go to")} ${pageLabel}${isActive ? ` (${t("current page")})` : ""}`}
                      >
                        <span className="text-lg">{pageLabel}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Settings Section */}
            <aside
              className="p-8 pb-10 border-gray-200 bg-white"
              aria-label={t("Page settings")}
            >
              <div className="flex items-center justify-start gap-4">
                {project?.settings?.languages?.length && (
                  <LanguageButton project={project} variant="outline" />
                )}
                {project?.settings?.accessibility && (
                  <AccessibilitySettings project={project} variant="outline" />
                )}
              </div>
            </aside>
          </div>
        </Transition>
      </Transition>
    </>
  );
};

export default InfoPageNav;
